import React, { useState, useRef } from 'react';
import { X, Upload, File, Loader2, CheckCircle, AlertCircle, Shield, FileText, GraduationCap, FolderPlus } from 'lucide-react';
import axios from 'axios';

const UploadEducationModal = ({ 
  isOpen, 
  onClose, 
  workerId, 
  onSuccess,
  documentType = 'marksheet',
  title = 'Upload Document',
  othersDocumentTypes = []
}) => {
  const [file, setFile] = useState(null);
  const [verificationNumber, setVerificationNumber] = useState('');
  const [othersDocumentType, setOthersDocumentType] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // Document type configurations
  const documentConfig = {
    marksheet: {
      label: 'Education Certificate',
      icon: GraduationCap,
      requiresFile: true,
      requiresVerificationId: false,
      fileRequired: true,
      verificationRequired: false,
      allowedTypes: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSize: 5 * 1024 * 1024,
    },
    resume: {
      label: 'Resume',
      icon: FileText,
      requiresFile: true,
      requiresVerificationId: false,
      fileRequired: true,
      verificationRequired: false,
      allowedTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
      maxSize: 5 * 1024 * 1024,
    },
    policeVerification: {
      label: 'Police Verification',
      icon: Shield,
      requiresFile: true, // Document is mandatory
      requiresVerificationId: false, // ID is optional
      fileRequired: true,
      verificationRequired: false,
      allowedTypes: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSize: 5 * 1024 * 1024,
    },
    eshram: {
      label: 'E-Shram Document',
      icon: FileText,
      requiresFile: true, // Document is mandatory for upload
      requiresVerificationId: false, // ID is handled outside
      fileRequired: true,
      verificationRequired: false,
      allowedTypes: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSize: 5 * 1024 * 1024,
    },
    others: {
      label: 'Others',
      icon: FolderPlus,
      requiresFile: true,
      requiresVerificationId: false,
      fileRequired: true,
      verificationRequired: true,
      allowedTypes: ['image/jpeg', 'image/png', 'application/pdf'],
      maxSize: 5 * 1024 * 1024,
    }
  };

  const config = documentConfig[documentType] || documentConfig.marksheet;
  const DocumentIcon = config.icon;

  const getModalTitle = () => {
    switch(documentType) {
      case 'marksheet': return 'Upload Education Certificate';
      case 'resume': return 'Upload Resume';
      case 'policeVerification': return 'Upload Police Verification';
      case 'eshram': return 'Upload E-Shram Document';
      case 'others': return 'Upload Other Document';
      default: return title;
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (file) => {
    if (file.size > config.maxSize) {
      setError(`File size exceeds ${config.maxSize / (1024 * 1024)}MB limit. Please choose a smaller file.`);
      return;
    }

    if (!config.allowedTypes.includes(file.type)) {
      const allowedExtensions = config.allowedTypes.map(type => {
        if (type.includes('jpeg')) return '.jpg/.jpeg';
        if (type.includes('png')) return '.png';
        if (type.includes('pdf')) return '.pdf';
        if (type.includes('msword')) return '.doc';
        if (type.includes('wordprocessingml')) return '.docx';
        return type;
      }).join(', ');
      setError(`Invalid file format. Please upload ${allowedExtensions} files.`);
      return;
    }

    setError(null);
    setFile(file);
    setSuccess(false);
  };

  const validateForm = () => {
    // Police Verification: Document is mandatory, ID is optional
    if (documentType === 'policeVerification') {
      if (!file) {
        setError('Please upload a police verification certificate.');
        return false;
      }
      return true;
    }

    // E-Shram: Document is mandatory (ID is handled outside)
    if (documentType === 'eshram') {
      if (!file) {
        setError('Please select a file to upload.');
        return false;
      }
      return true;
    }

    if (documentType === 'others') {
      if (!file) {
        setError('Please select a file to upload.');
        return false;
      }
      if (!othersDocumentType) {
        setError('Please select a document type.');
        return false;
      }
      return true;
    }

    if (!file) {
      setError(`Please select a file to upload.`);
      return false;
    }

    return true;
  };

  const handleUpload = async () => {
    if (!validateForm()) {
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const token = localStorage.getItem("superadmintoken");
      if (!token) {
        throw new Error('Authentication required. Please login again.');
      }

      const formData = new FormData();
      
      if (file) {
        formData.append('file', file);
      }
      
      formData.append('documentType', documentType);
      
      // Only send verificationId for police verification if provided
      if (documentType === 'policeVerification' && verificationNumber.trim()) {
        formData.append('verificationId', verificationNumber.trim());
      }

      // For others document type, send the selected document type name
      if (documentType === 'others' && othersDocumentType) {
        formData.append('name', othersDocumentType);
        formData.append('othersDocumentType', othersDocumentType);
      }

      const response = await axios.post(
        `${BASE_URL}/lenskart/upload-worker-document/${workerId}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(progress);
            }
          },
        }
      );

      if (response.data.success) {
        setSuccess(true);
        setUploadProgress(100);
        
        if (onSuccess) {
          const responseData = {
            ...response.data.data,
            documentType: documentType === 'others' ? othersDocumentType : documentType,
            documentTypeName: documentType === 'others' ? othersDocumentType : undefined,
          };
          onSuccess(responseData);
        }

        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.response?.data?.message || error.message || 'Failed to upload document. Please try again.');
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setFile(null);
      setVerificationNumber('');
      setOthersDocumentType('');
      setError(null);
      setSuccess(false);
      setUploadProgress(0);
      setIsDragging(false);
      onClose();
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileAcceptance = () => {
    return config.allowedTypes.join(',');
  };

  const getSupportedFormats = () => {
    return config.allowedTypes.map(type => {
      if (type.includes('jpeg')) return 'JPEG';
      if (type.includes('png')) return 'PNG';
      if (type.includes('pdf')) return 'PDF';
      if (type.includes('msword') || type.includes('wordprocessingml')) return 'DOC/DOCX';
      return type;
    }).join(', ');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1200] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <DocumentIcon className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">{getModalTitle()}</h2>
          </div>
          <button 
            onClick={handleClose} 
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            disabled={isUploading}
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {success ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Successful!</h3>
              <p className="text-sm text-gray-500 text-center">
                Your {config.label.toLowerCase()} has been uploaded successfully.
              </p>
            </div>
          ) : (
            <>
              {/* Document Type Dropdown for Others */}
              {documentType === 'others' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={othersDocumentType}
                    onChange={(e) => setOthersDocumentType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    disabled={isUploading}
                  >
                    <option value="">Select document type...</option>
                    {othersDocumentTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    Please select the type of document you are uploading
                  </p>
                </div>
              )}

              {/* File Upload Area */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Document <span className="text-red-500">*</span>
                </label>
                <div
                  className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragging 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : file 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-300 hover:border-[#2563EB]'
                  }`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    onChange={handleFileSelect}
                    accept={getFileAcceptance()}
                    disabled={isUploading}
                  />

                  {file ? (
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                        <File className="h-6 w-6 text-green-600" />
                      </div>
                      <p className="text-sm font-medium text-gray-900 mb-1">{file.name}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      <button
                        onClick={handleRemoveFile}
                        className="mt-3 text-sm text-red-600 hover:text-red-800 font-medium"
                        disabled={isUploading}
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                        <Upload className="h-6 w-6 text-gray-600" />
                      </div>
                      <p className="text-sm text-gray-600 font-medium">
                        Drag & drop your file here 
                      </p>
                      <p className="text-sm text-[#2563EB] font-medium">
                        or click to browse
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Supported formats: {getSupportedFormats()} (Max {config.maxSize / (1024 * 1024)}MB)
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Police Verification Number Field - Optional */}
              {documentType === 'policeVerification' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Police Verification Number
                    <span className="text-xs text-gray-500 ml-2">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={verificationNumber}
                    onChange={(e) => setVerificationNumber(e.target.value)}
                    placeholder="Enter verification number (optional)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    disabled={isUploading}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Enter the police verification reference number (optional)
                  </p>
                </div>
              )}

              {/* Upload Progress */}
              {isUploading && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* File Info */}
              {file && !isUploading && !error && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span>File ready to upload: <strong>{file.name}</strong></span>
                  </div>
                </div>
              )}

              {/* Validation Info for Police Verification */}
              {documentType === 'policeVerification' && !file && !isUploading && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-700">
                    Please upload a police verification certificate. Verification number is optional.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-gray-50 rounded-b-xl">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={
              isUploading || 
              success || 
              !file
            }
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : success ? (
              'Uploaded ✓'
            ) : (
              'Upload'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadEducationModal;